import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useState } from 'react';
// import { TransformControls as TransformControlsStd } from 'three-stdlib';
import { TransformControls as TransformControlsStd } from './StdLib/TransformControls';
import type { TransformTarget } from '../types';
import { useAbyssViewerContext } from './AbyssViewerContext';
import { TransformControlsMode, TransformSpace } from './Enums';
import type { Object3DEventMap } from 'three';

export interface TransformControlsProps {
  target: TransformTarget | undefined;
  mode: TransformControlsMode;
  space: TransformSpace;
  size: number;
  showX?: boolean;
  showY?: boolean;
  showZ?: boolean;
  onChange?: () => void;
}

export const TransformControls = ({
  target,
  mode,
  space,
  size,
  showX = true,
  showY = true,
  showZ = true,
  onChange,
}: TransformControlsProps) => {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const { domElement } = gl;
  const scene = useThree((state) => state.scene);
  const invalidate = useThree((state) => state.invalidate);
  const regress = useThree((state) => state.performance.regress);

  const { navigationControls } = useAbyssViewerContext();

  const [controls, setControls] = useState<TransformControlsStd | undefined>();

  const handleChange = useCallback(() => {
    onChange?.();
    invalidate();
    regress();
  }, [onChange, invalidate, regress]);

  useEffect(() => {
    if (!navigationControls) return () => {};

    const newControls = new TransformControlsStd(camera, domElement);
    scene.add(newControls);
    setControls(newControls);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callback = (event: any) => navigationControls?.setIsEnabled(!event.value);
    newControls.addEventListener('dragging-changed' as keyof Object3DEventMap, callback);

    newControls.addEventListener('change' as keyof Object3DEventMap, handleChange);

    return () => {
      newControls.removeEventListener('dragging-changed' as keyof Object3DEventMap, callback);
      newControls.removeEventListener('change' as keyof Object3DEventMap, handleChange);
      scene.remove(newControls);
      setControls(undefined);
      newControls.dispose();
    };
  }, [camera, scene, domElement, navigationControls, handleChange]);

  useEffect(() => {
    if (controls) {
      controls.detach();
      if (target) {
        const { targetGroup } = target;
        controls.attach(targetGroup);
        controls.updateMatrixWorld();
        invalidate();
      }
    }
  }, [controls, target, invalidate]);

  useEffect(() => {
    if (controls) {
      switch (mode) {
        case TransformControlsMode.Translate:
          controls.setMode('translate');
          break;
        case TransformControlsMode.Rotate:
          controls.setMode('rotate');
          break;
        case TransformControlsMode.Scale:
          controls.setMode('scale');
          break;
        default:
          break;
      }
      invalidate();
    }
  }, [controls, mode, invalidate]);

  useEffect(() => {
    if (controls) {
      switch (space) {
        case TransformSpace.Local:
          controls.setSpace('local');
          break;
        case TransformSpace.World:
          controls.setSpace('world');
          break;
        default:
          break;
      }
      invalidate();
    }
  }, [controls, space, invalidate]);

  useEffect(() => {
    if (controls) {
      controls.setSize(size);
      invalidate();
    }
  }, [controls, size, invalidate]);

  useEffect(() => {
    if (controls) {
      controls.showX = showX;
      invalidate();
    }
  }, [controls, showX, invalidate]);

  useEffect(() => {
    if (controls) {
      controls.showY = showY;
      invalidate();
    }
  }, [controls, showY, invalidate]);

  useEffect(() => {
    if (controls) {
      controls.showZ = showZ;
      invalidate();
    }
  }, [controls, showZ, invalidate]);

  return <></>;
};
